/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * WorkspaceTabs.tsx — Workspac\1 \2abs (services/operator-web-app).
 */

/**
 * Центральная область с табами: Поля сценария, Место встречи, Встреча, Адрес, Завершение звонка, Метрики.
 * Иконка таба мигает при автоматическом определении контекста (copilot highlight).
 */
import { useState } from 'react';
import { useOperatorStore } from '@/store';
import type { HighlightWidget } from '@/store/slices/copilotSlice';
import {
  ScenarioFieldsPanel,
  CalendarWidget,
  MeetingSummary,
  AddressWidget,
  WrapUpPanel,
} from './index';
import { MetricsPanel } from '@/observability';
import { HighlightBox } from '@/features/copilot';
import { useNow } from '@/hooks/useNow';
import { useTranslation } from '@/i18n';

type TabId = 'scenario' | 'meeting_place' | 'meeting' | 'address' | 'wrap_up' | 'metrics';

interface TabDef {
  id: TabId;
  labelKey: string;
  widgetId: HighlightWidget | null;
  icon: React.ReactNode;
  content: React.ReactNode;
}

// Иконки — простые SVG (24x24 viewBox)
const IconScenario = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);
const IconMeetingPlace = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);
const IconCalendar = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);
const IconAddress = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const IconWrapUp = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
  </svg>
);
const IconMetrics = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const TABS: TabDef[] = [
  {
    id: 'scenario',
    labelKey: 'tabs.scenario',
    widgetId: 'customer_card_fields',
    icon: <IconScenario />,
    content: <ScenarioFieldsPanel />,
  },
  {
    id: 'meeting_place',
    labelKey: 'tabs.meeting_place',
    widgetId: 'meeting_summary',
    icon: <IconMeetingPlace />,
    content: (
      <HighlightBox widgetId="meeting_summary">
        <MeetingSummary />
      </HighlightBox>
    ),
  },
  {
    id: 'meeting',
    labelKey: 'tabs.meeting',
    widgetId: 'calendar_widget',
    icon: <IconCalendar />,
    content: <CalendarWidget />,
  },
  {
    id: 'address',
    labelKey: 'tabs.address',
    widgetId: 'map_widget',
    icon: <IconAddress />,
    content: <AddressWidget />,
  },
  {
    id: 'wrap_up',
    labelKey: 'tabs.wrap_up',
    widgetId: 'wrap_up_panel',
    icon: <IconWrapUp />,
    content: <WrapUpPanel />,
  },
  {
    id: 'metrics',
    labelKey: 'tabs.metrics',
    widgetId: null,
    icon: <IconMetrics />,
    content: <MetricsPanel />,
  },
];

export function WorkspaceTabs() {
  const t = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('scenario');
  const highlight = useOperatorStore((s) => s.copilot.highlight);
  const now = useNow(highlight ? 500 : 30000); // тик каждые 500 мс при активном highlight, иначе реже (снять мигание по TTL)

  return (
    <div
      className="flex flex-col flex-1 min-h-0 rounded-lg border overflow-hidden"
      style={{ borderColor: 'var(--glass-border)', background: 'var(--glass-bg-from)' }}
    >
      <div
        className="flex items-center gap-0 border-b shrink-0"
        style={{ borderColor: 'var(--glass-border)', background: 'var(--toggle-bg)' }}
        role="tablist"
        aria-label={t('tabs.label')}
      >
        {TABS.map((tab) => (
          <TabButton
            key={tab.id}
            tab={tab}
            label={t(tab.labelKey)}
            isActive={activeTab === tab.id}
            onSelect={() => setActiveTab(tab.id)}
            now={now}
          />
        ))}
      </div>
      <div
        id="workspace-tabpanel"
        className="flex-1 overflow-auto p-4 min-h-0"
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
      >
        {TABS.find((t) => t.id === activeTab)?.content}
      </div>
    </div>
  );
}

function TabButton({
  tab,
  label,
  isActive,
  onSelect,
  now,
}: {
  tab: TabDef;
  label: string;
  isActive: boolean;
  onSelect: () => void;
  now: number;
}) {
  const highlight = useOperatorStore((s) => s.copilot.highlight);
  const isHighlighted =
    tab.widgetId &&
    highlight &&
    highlight.targetWidget === tab.widgetId &&
    now <= highlight.expiresAt;

  return (
    <button
      id={`tab-${tab.id}`}
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-controls="workspace-tabpanel"
      tabIndex={isActive ? 0 : -1}
      onClick={onSelect}
      title={label}
      className={`
        flex items-center justify-center w-12 h-12 shrink-0 border-b-2 transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-inset
        ${isActive ? 'border-sky-500 text-sky-600' : 'border-transparent'}
        ${isHighlighted ? 'animate-blink' : ''}
      `}
      style={isActive ? {} : { color: 'var(--text-muted)' }}
    >
      {tab.icon}
    </button>
  );
}
